'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import type { PaginationMeta } from '@eliadmin/core/src/api/api-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselControls, CarouselControlsPlaceholder } from '@eliadmin/core/src/components/ui/carousel-controls';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import {
  buildProductsQuery,
  type BuildProductQueryOptions,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { EmblaCarouselType } from 'embla-carousel';
import Link from '@eliadmin/core/src/components/link';
import { Suspense, useMemo, useRef, type ComponentPropsWithoutRef } from 'react';
import type { ProductSegment } from '@eliadmin/core/src/api/product-segments/product-segments-api';
import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { ProductCarousel } from './product-carousel';
import { ProductCarouselAutoplay } from './product-carousel-autoplay';

const PRODUCT_SEGMENT_STALE_TIME_MS = 1000 * 60 * 5;

function ProductSegmentSkeletonCard() {
  return (
    <Skeleton
      className={cn(
        'h-[315px] w-full min-w-0 rounded-3xl shadow-lg',
        'ea-product-labels-container__skeleton-card',
      )}
    />
  );
}

export type ProductSegmentsContainerProps = {
  query?: BuildProductQueryOptions;
  segmentType: 'discounted' | 'our-selection' | 'newest';
  initialSegment?: ProductSegment;
  initialProducts?: { data: Product[]; meta?: PaginationMeta };
  initialProductsArePublic: boolean;
  title?: string;
  id?: string;
  className?: string;
  before?: React.ReactNode;
  after?: React.ReactNode;
  style?: React.CSSProperties;
  carouselProps?: Omit<ComponentPropsWithoutRef<typeof ProductCarousel>, 'products'>;
  children?: React.ReactNode;
  buttonLabel?: string;
  showMoreLink?: boolean;
};

function ProductSegmentsInternal({
  query: queryOpts,
  segmentType,
  ...props
}: ProductSegmentsContainerProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const { me, isInitialized } = useCustomerStore();
  const { t } = useTranslations();

  const emblaRef = useRef<EmblaCarouselType>(null!);
  const sectionRef = useRef<HTMLElement>(null);

  const { data: segments } = useSuspenseQuery({
    queryKey: ['PRODUCT_SEGMENTS', segmentType, locale],
    queryFn: () =>
      import('@eliadmin/core/src/api/product-segments/product-segments-api').then(api => api.fetchProductSegmentsByType({
        locale,
        segmentType,
      })),
    staleTime: PRODUCT_SEGMENT_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialData: props.initialSegment ? { data: [props.initialSegment] } : undefined,
  });

  const displaySegment: ProductSegment | undefined = segments?.data?.[0];

  const query = useMemo(() => buildProductsQuery({ ...queryOpts }), [queryOpts]);
  const { data: products, isError, refetch } = useQuery({
    queryKey: [
      CacheKey.PRODUCT_SEGMENT_PRODUCTS,
      segmentType,
      displaySegment?.id ?? null,
      // TanStack hashes the complete normalized input; HTTP serialization is needed
      // only inside the original API call, not to render already seeded products.
      query,
      locale,
      me?.customer.id ?? 'guest',
    ],
    queryFn: () => {
      if (!displaySegment?.id) return { data: [] };

      return import('@eliadmin/core/src/api/product-segments/product-segments-api').then(api => api.fetchProductSegmentProducts({
        locale,
        productSegmentId: displaySegment.id,
        query,
        authToken: me?.authToken,
      }));
    },
    enabled: isInitialized && !!displaySegment?.id,
    staleTime: PRODUCT_SEGMENT_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialData:
      props.initialProductsArePublic && !me?.authToken ? props.initialProducts : undefined,
    placeholderData:
      !props.initialProductsArePublic && (!isInitialized || !!me?.authToken)
        ? props.initialProducts
        : undefined,
  });

  // A transient failure is not a successful empty result. TanStack keeps any
  // existing healthy data; an unseeded customer request exposes a retry instead.
  if (!products?.data?.length && isError) return (
    <section id={props.id} className={props.className} style={props.style}>
      <Container size="xl">
        <h2 className="text-2xl font-bold">{props.title}</h2>
        <p role="alert">{t('text1320')}</p>
        <Button onClick={() => void refetch()}>{t('text595')}</Button>
      </Container>
    </section>
  );
  if (!products?.data?.length) return null;

  return (
    <section
      ref={sectionRef}
      id={props.id}
      className={cn('ea-product-labels-container', props.className)}
      style={props.style}
    >
      <CarouselProvider>
        <ProductCarouselAutoplay containerRef={sectionRef} />
        <Container
          size="xl"
          className={cn('flex flex-col gap-6', 'ea-product-labels-container__container')}
        >
          <div
            className={cn(
              'flex min-h-9 flex-row items-center justify-between gap-4',
              'ea-product-labels-container__header',
            )}
          >
            <h2
              className={cn('min-w-0 break-words text-left text-2xl font-bold', 'ea-product-labels-container__title')}
            >
              {props.title}
            </h2>
            <CarouselControls />
          </div>
          <div
            className={cn(
              'flex flex-row items-stretch gap-4',
              'ea-product-labels-container__content',
            )}
          >
            {props.before}
            <ProductCarousel
              {...props.carouselProps}
              emblaRef={emblaRef}
              products={products.data}
            />
            {props.after}
          </div>

          {props.showMoreLink && (
            <div
              className={cn(
                'flex flex-row items-center justify-center',
                'ea-product-labels-container__button-wrapper',
              )}
            >
              <Button
                variant="accent"
                asChild
                className={cn('ea-product-labels-container__button')}
              >
                <Link href={displaySegment?.slug ? `/${displaySegment.slug}` : '#'}>
                  {props.title ?? t('text338')}
                </Link>
              </Button>
            </div>
          )}

          {props.children}
        </Container>
      </CarouselProvider>
    </section>
  );
}

export function ProductSegmentsContainer(props: ProductSegmentsContainerProps) {
  const loader = (
    <section
      id={props.id}
      className={cn(props.className, 'ea-product-labels-container')}
      style={props.style}
    >
      <Container
        size="xl"
        className={cn('flex flex-col space-y-6', 'ea-product-labels-container__container')}
      >
        <div className="ea-product-labels-container__header flex min-h-9 items-center justify-between gap-4">
          <h2 className="ea-product-labels-container__title min-w-0 break-words text-left text-2xl font-bold">
            {props.title}
          </h2>
          <CarouselControlsPlaceholder />
        </div>
        <div
          className={cn(
            'flex flex-row items-stretch gap-4',
            'ea-product-labels-container__content',
          )}
        >
          {props.before}
          <div className={cn('flex w-full gap-1', 'ea-carousel__wrapper ea-product-carousel')}>
            <div className={cn('relative w-full overflow-hidden', 'ea-carousel')}>
              <div className={cn('flex h-full min-w-full', 'ea-carousel__container')}>
                {[...Array(5)].map((_, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      'h-full w-full flex-shrink-0 flex-grow-0 basis-full p-2 sm:basis-1/2 md:basis-1/3 lg:basis-1/4',
                      'ea-carousel__slide',
                    )}
                  >
                    <ProductSegmentSkeletonCard />
                  </div>
                ))}
              </div>
            </div>
          </div>
          {props.after}
        </div>
      </Container>
    </section>
  );

  return (
    <Suspense fallback={loader}>
      <ProductSegmentsInternal {...props} />
    </Suspense>
  );
}
